import { useEffect, useState } from 'react';

import { Button, Input, Spinner, Switch } from '@components';
import { ErrorCode } from '@constants/errorCodes';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import {
  useCreateLeadIngestionProviderMutation,
  useGetLeadIngestionProviderQuery,
  useGetLeadIngestionWebhookConfigQuery,
  useUpdateLeadIngestionProviderMutation
} from '@features/lead-management/queries/LeadManagementQuery';
import {
  buildProviderSaveBody,
  getMetaProviderErrorMessage,
  getUnsubscribedPages
} from '@features/lead-management/utils/metaIntegrationHelpers';
import useNotify from '@hooks/notify';
import { getLocalStorageItem } from '@utils/storage';

import MetaAccessTokenBanner from './MetaAccessTokenBanner';
import { INSTAGRAM_STEPS, MESSENGER_STEPS, Step } from './MetaAccessTokenSteps';
import { LeadIngestionCredentials, LeadIngestionProviderType } from './types';

type CredentialField = keyof LeadIngestionCredentials;

const FIELDS: Record<CredentialField, { label: string; placeholder: string }> =
  {
    access_token: {
      label: 'Access token',
      placeholder: 'Enter access token'
    },
    app_id: { label: 'App ID', placeholder: 'Enter app ID' },
    app_secret: { label: 'App secret', placeholder: 'Enter app secret' }
  };

const CHANNELS: Record<
  LeadIngestionProviderType,
  {
    label: string;
    fields: CredentialField[];
    steps: Step[];
    webhookErrorMessage: string;
  }
> = {
  instagram: {
    label: 'Instagram',
    fields: ['access_token', 'app_secret'],
    steps: INSTAGRAM_STEPS,
    webhookErrorMessage:
      "Your Instagram app's webhook is not subscribed to the messages field. Add the webhook URL and verify token below under Webhooks, subscribe to messages, then try again."
  },
  messenger: {
    label: 'Messenger',
    fields: ['access_token', 'app_id', 'app_secret'],
    steps: MESSENGER_STEPS,
    webhookErrorMessage:
      "Your Meta app's Page webhook is not set up. Add the webhook URL and verify token below to your app's Page webhook subscription, then try again."
  }
};

const EMPTY_CREDENTIALS: LeadIngestionCredentials = {
  access_token: '',
  app_id: '',
  app_secret: ''
};

// Backend masks all but the last 4 characters; long-lived tokens would render
// as a wall of asterisks, so cap the visible mask.
const shortenMask = (value = '') =>
  value.replace(/^\*+/, (mask) => '*'.repeat(Math.min(mask.length, 10)));

const labelStyle = 'text-sm font-semibold tracking-[-0.2px] text-greyText1';

const editableInputStyle =
  'h-12.5 w-full rounded-lg border border-grey30 px-4 py-2.5 text-base font-normal leading-none tracking-normal placeholder:text-cofeeNeutral6 focus:border-cofeePrimary6 focus:outline-none disabled:cursor-not-allowed disabled:bg-grey20 disabled:text-grey60';

const readOnlyInputStyle =
  'h-12.5 w-full rounded-lg border border-grey30 bg-grey10 px-4 py-2.5 pr-10 text-base font-normal leading-none tracking-normal text-grey80';

const WebhookField = ({ label, value }: { label: string; value: string }) => (
  <div className="space-y-2">
    <label className={labelStyle}>{label}</label>
    <Input
      type="text"
      value={value}
      readOnly
      showTooltip
      textToCopy={value}
      className="w-full max-w-[430px]"
      inputStyle={readOnlyInputStyle}
    />
  </div>
);

interface MetaChannelIntegrationProps {
  providerType: LeadIngestionProviderType;
}

const MetaChannelIntegration = ({
  providerType
}: MetaChannelIntegrationProps) => {
  const { label, fields, steps, webhookErrorMessage } = CHANNELS[providerType];
  const organizationId =
    getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID) || '';
  const args = { organizationId, providerType };
  const { createSuccessAlert, createErrorAlert } = useNotify();

  const providerQuery = useGetLeadIngestionProviderQuery(args, {
    skip: !organizationId
  });
  const webhookQuery = useGetLeadIngestionWebhookConfigQuery(args, {
    skip: !organizationId
  });
  const { data: provider } = providerQuery;
  const webhookConfig = webhookQuery.data;
  const [createProvider, { isLoading: isCreating }] =
    useCreateLeadIngestionProviderMutation();
  const [updateProvider, { isLoading: isUpdating }] =
    useUpdateLeadIngestionProviderMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [credentials, setCredentials] = useState(EMPTY_CREDENTIALS);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (provider) setIsActive(provider.is_active);
  }, [provider]);

  const isFormOpen = !provider || isEditing;
  const isSaving = isCreating || isUpdating;
  const saveBody = buildProviderSaveBody(fields, credentials);
  const filledCount = Object.keys(saveBody).length;
  const canSave = provider ? filledCount > 0 : filledCount === fields.length;
  // Backends without DM ingestion ignore provider_type and return the lead
  // forms provider, and have no webhook-config route. Never act on those.
  const isUnavailable =
    providerQuery.isError ||
    webhookQuery.isError ||
    (!!provider && provider.provider !== providerType);

  const closeForm = () => {
    setIsEditing(false);
    setCredentials(EMPTY_CREDENTIALS);
  };

  const handleToggle = async (value: boolean) => {
    setIsActive(value);
    if (!provider) return;
    try {
      await updateProvider({
        ...args,
        providerId: provider.id,
        body: { is_active: value }
      }).unwrap();
      createSuccessAlert(
        `Lead syncing from ${label} ${
          value ? 'enabled' : 'paused'
        } successfully!`
      );
    } catch {
      setIsActive(!value);
      createErrorAlert('Failed to update lead syncing status.');
    }
  };

  const handleSave = async () => {
    try {
      const response = provider
        ? await updateProvider({
            ...args,
            providerId: provider.id,
            body: saveBody
          }).unwrap()
        : await createProvider({
            ...args,
            body: { ...saveBody, is_active: isActive }
          }).unwrap();
      closeForm();
      // Only the latest toast is shown, so a partial failure gets one message.
      const unsubscribedPages = getUnsubscribedPages(response);
      if (unsubscribedPages.length) {
        createErrorAlert(
          `${label} saved, but these pages could not be subscribed: ${unsubscribedPages.join(
            ', '
          )}`,
          { classOverride: 'max-w-xl' }
        );
      } else {
        createSuccessAlert(`${label} integration saved successfully!`);
      }
    } catch (error) {
      createErrorAlert(
        getMetaProviderErrorMessage(error, {
          [ErrorCode.ERR_MISSING_WEBHOOK_SUBSCRIPTION]: webhookErrorMessage
        }),
        { classOverride: 'max-w-xl' }
      );
    }
  };

  if (providerQuery.isLoading || webhookQuery.isLoading) {
    return (
      <div className="flex min-h-70 flex-col items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (isUnavailable) {
    return (
      <div className="flex min-h-70 flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-base text-grey80">
          {label} lead syncing is not available right now. Please try again
          later.
        </p>
        <Button
          variant="tertiary"
          className="px-4 py-2"
          handleClick={() => {
            providerQuery.refetch();
            webhookQuery.refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <Switch
            value={isActive}
            handleClick={handleToggle}
            disabled={isUpdating}
            testId={`${providerType}-lead-sync`}
          />
          <span className="text-base font-semibold text-greyText1">
            Enable lead syncing from {label}
          </span>
        </div>
        <p className="pl-12 text-sm text-grey80">
          Pause or resume automatic ingestion of Meta leads from {label}.
        </p>
      </div>

      <div className="space-y-6 pl-12">
        {fields.map((field, index) => (
          <div key={field} className="space-y-2">
            <label className={labelStyle}>
              {FIELDS[field].label}
              <span className="text-error"> *</span>
            </label>
            <Input
              type={isFormOpen && field === 'app_secret' ? 'password' : 'text'}
              value={
                isFormOpen ? credentials[field] : shortenMask(provider?.[field])
              }
              handleInputChange={(value) =>
                setCredentials((prev) => ({ ...prev, [field]: value }))
              }
              placeholder={
                shortenMask(provider?.[field]) || FIELDS[field].placeholder
              }
              disabled={!isFormOpen}
              className="w-full max-w-[430px]"
              inputStyle={editableInputStyle}
            />
            {index === 0 && (
              <MetaAccessTokenBanner
                steps={steps}
                wrapperClassName="max-w-[430px]"
                bannerClassName="!px-0.5 hover:opacity-80"
              />
            )}
          </div>
        ))}

        <div className="space-y-3">
          <div className="space-y-1">
            <p className="text-base font-semibold text-greyText1">
              Configure Webhook in your meta account
            </p>
            <p className="text-sm text-grey80">
              Copy the below webhook url and verify token and paste it into meta
              account
            </p>
          </div>
          <WebhookField
            label="Webhook URL"
            value={webhookConfig?.webhook_url ?? ''}
          />
          <WebhookField
            label="Verify token"
            value={webhookConfig?.verify_token ?? ''}
          />
        </div>

        <div className="flex gap-3">
          {isFormOpen ? (
            <>
              <Button
                variant="primary"
                className="px-4 py-2 text-white"
                handleClick={handleSave}
                disabled={!canSave || isSaving}
                loading={isSaving}
              >
                Verify and save
              </Button>
              {provider && (
                <Button
                  variant="tertiary"
                  className="px-4 py-2"
                  handleClick={closeForm}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="primary"
              className="px-4 py-2 text-white"
              handleClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetaChannelIntegration;
